import React, { useEffect, useMemo, useState } from "react";
import { loadVehicleTypes, getIconStyle, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UserCheck,
  RefreshCw,
  AlertCircle,
  Car,
  Truck,
  Clock,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import type { ShiftRow, DriverRow, VehicleRow, ShiftTypeRow } from "./types";
import { calcWorkedHours, combineDateAndTime, formatTime12, driverDisplayName } from "./utils";

interface FindCoverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  absentShift: ShiftRow | null;
  drivers: DriverRow[];
  vehicles: VehicleRow[];
  shifts: ShiftRow[];
  shiftTypes: ShiftTypeRow[];
  unavailableVehicleKeys: Set<string>; // key = "shift_date|vehicle_number"
  onCoverCreated: () => void;
}

const STANDBY_CODE = "STANDBY";

const FindCoverDialog: React.FC<FindCoverDialogProps> = ({
  open,
  onOpenChange,
  absentShift,
  drivers,
  vehicles,
  shifts,
  shiftTypes,
  unavailableVehicleKeys,
  onCoverCreated,
}) => {
  // Default times: use the absent shift's times unless it's a full day (00:00–23:59)
  const defaultStart = useMemo(() => {
    if (!absentShift) return "08:00";
    const s = absentShift.start_hhmm;
    const e = absentShift.end_hhmm;
    if (s === "00:00" && (e === "23:59" || e === "24:00")) return "08:00";
    return s;
  }, [absentShift]);

  const defaultEnd = useMemo(() => {
    if (!absentShift) return "17:00";
    const s = absentShift.start_hhmm;
    const e = absentShift.end_hhmm;
    if (s === "00:00" && (e === "23:59" || e === "24:00")) return "17:00";
    return e;
  }, [absentShift]);

  // Best guess "usual vehicle" for the absent driver, based on most-used
  // vehicle across their recent non-cancelled shifts in the loaded window.
  const usualVehicle = useMemo<VehicleRow | null>(() => {
    if (!absentShift) return null;
    const counts = new Map<string, number>();
    for (const s of shifts) {
      if (s.driver_name !== absentShift.driver_name) continue;
      if (s.status === "cancelled") continue;
      if (!s.vehicle_number) continue;
      counts.set(s.vehicle_number, (counts.get(s.vehicle_number) || 0) + 1);
    }
    let best: { num: string; count: number } | null = null;
    for (const [num, count] of counts.entries()) {
      if (!best || count > best.count) best = { num, count };
    }
    if (!best) return null;
    return vehicles.find((v) => v.vehicle_number === best!.num) || null;
  }, [absentShift, shifts, vehicles]);

  // Editable form state — initialised lazily when dialog opens
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);
  const [breakMinutes, setBreakMinutes] = useState(30);
  const [vehicleNumber, setVehicleNumber] = useState<string>("");
  const [shiftTypeCode, setShiftTypeCode] = useState<string>("");
  const [saving, setSaving] = useState<string | null>(null);

  // (Re-)initialise when dialog opens
  React.useEffect(() => {
    if (!open || !absentShift) return;
    setStart(defaultStart);
    setEnd(defaultEnd);
    setBreakMinutes(absentShift.break_minutes ?? 30);
    setVehicleNumber(usualVehicle?.vehicle_number || "");
    // Pick the first active shift-type that counts as worked hours
    const def = shiftTypes.find(
      (st) => st.is_active && st.counts_as_worked_hours !== false
    );
    setShiftTypeCode(def?.code || shiftTypes[0]?.code || "");
  }, [open, absentShift, defaultStart, defaultEnd, usualVehicle, shiftTypes]);

  const dateStr = absentShift?.shift_date || "";

  // Available vehicles for that day (not unavailable, not assigned to an
  // active shift that day). Plus the usual vehicle is always eligible since
  // that's exactly the seat we're trying to fill.
  const availableVehicles = useMemo(() => {
    if (!absentShift) return [] as VehicleRow[];
    const assignedThatDay = new Set<string>();
    for (const s of shifts) {
      if (s.shift_date !== dateStr) continue;
      if (s.status === "cancelled") continue;
      if (!s.vehicle_number) continue;
      // If this is the absent driver's own row we don't count it
      if (s.id === absentShift.id) continue;
      assignedThatDay.add(s.vehicle_number);
    }
    return vehicles.filter((v) => {
      const key = `${dateStr}|${v.vehicle_number}`;
      if (unavailableVehicleKeys.has(key)) return false;
      const status = (v.status || "").toString().trim().toLowerCase();
      if (status === "maintenance") return false;
      if (assignedThatDay.has(v.vehicle_number)) {
        // Allow if it's the usual vehicle for the absent driver
        if (usualVehicle && usualVehicle.vehicle_number === v.vehicle_number) {
          return true;
        }
        return false;
      }
      return true;
    });
  }, [absentShift, dateStr, vehicles, shifts, unavailableVehicleKeys, usualVehicle]);

  const selectedVehicle = useMemo<VehicleRow | null>(() => {
    if (!vehicleNumber) return null;
    return vehicles.find((v) => v.vehicle_number === vehicleNumber) || null;
  }, [vehicleNumber, vehicles]);

  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);
  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
  }, []);
  const selectedVehicleIconIsTruck = getIconStyle(selectedVehicle?.vehicle_type, vehicleTypes) === "truck";

  // Candidate cover drivers: active, not the absent driver, either no shift
  // that day OR only shifts that are STANDBY. Can drive the selected vehicle
  // (manual check if vehicle is manual transmission).
  const candidateDrivers = useMemo(() => {
    if (!absentShift) return [] as Array<{ driver: DriverRow; status: "free" | "standby"; standbyShift?: ShiftRow }>;

    // Build map: driver_name -> shifts that day (excluding cancelled)
    const byDriver = new Map<string, ShiftRow[]>();
    for (const s of shifts) {
      if (s.shift_date !== dateStr) continue;
      if (s.status === "cancelled") continue;
      const arr = byDriver.get(s.driver_name) || [];
      arr.push(s);
      byDriver.set(s.driver_name, arr);
    }

    const out: Array<{ driver: DriverRow; status: "free" | "standby"; standbyShift?: ShiftRow }> = [];
    for (const d of drivers) {
      if (!d.is_active) continue;
      if (d.driver_name === absentShift.driver_name) continue;

      const theirShifts = byDriver.get(d.driver_name) || [];

      if (theirShifts.length === 0) {
        // Fully free
        if (selectedVehicle?.transmission_type?.toLowerCase() === "manual" && !d.can_drive_manual) {
          continue;
        }
        out.push({ driver: d, status: "free" });
        continue;
      }

      // Only allow if every shift that day is STANDBY
      const allStandby = theirShifts.every(
        (s) => (s.shift_type_code || "").toUpperCase() === STANDBY_CODE
      );
      if (!allStandby) continue;

      if (selectedVehicle?.transmission_type?.toLowerCase() === "manual" && !d.can_drive_manual) {
        continue;
      }
      out.push({ driver: d, status: "standby", standbyShift: theirShifts[0] });
    }

    out.sort((a, b) => {
      // Free drivers first, then standby
      if (a.status !== b.status) return a.status === "free" ? -1 : 1;
      return a.driver.driver_name.localeCompare(b.driver.driver_name);
    });
    return out;
  }, [absentShift, drivers, shifts, dateStr, selectedVehicle]);

  const displayName = (d: DriverRow): string => {
    if (d.driver_firstname && d.driver_lastname) {
      return `${d.driver_firstname} ${d.driver_lastname.charAt(0)}.`;
    }
    return d.driver_name;
  };

  const handleAssign = async (cover: { driver: DriverRow; status: "free" | "standby"; standbyShift?: ShiftRow }) => {
    if (!absentShift) return;
    if (!shiftTypeCode) {
      toast({ title: "Missing", description: "Pick a shift type first", variant: "destructive" });
      return;
    }
    if (start >= end) {
      toast({ title: "Invalid times", description: "End time must be after start time", variant: "destructive" });
      return;
    }

    setSaving(cover.driver.driver_name);
    try {
      const workedHours = calcWorkedHours(start, end, breakMinutes);
      const payload = {
        driver_name: cover.driver.driver_name,
        vehicle_number: vehicleNumber || null,
        shift_type_code: shiftTypeCode,
        status: "published" as const,
        shift_date: dateStr,
        start_at: combineDateAndTime(dateStr, start),
        end_at: combineDateAndTime(dateStr, end),
        start_hhmm: start,
        end_hhmm: end,
        break_minutes: breakMinutes,
        worked_hours: workedHours,
        admin_notes: `Cover for ${driverDisplayName(absentShift)} (${absentShift.shift_type_label})`,
        published_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // If the cover driver is on STANDBY that day, convert that standby
      // shift into this cover shift instead of creating a duplicate row.
      if (cover.status === "standby" && cover.standbyShift) {
        const { error } = await supabase
          .from("roster_shifts")
          .update({
            vehicle_number: vehicleNumber || null,
            shift_type_code: shiftTypeCode,
            status: "published",
            start_at: combineDateAndTime(dateStr, start),
            end_at: combineDateAndTime(dateStr, end),
            start_hhmm: start,
            end_hhmm: end,
            break_minutes: breakMinutes,
            worked_hours: workedHours,
            admin_notes: payload.admin_notes,
            published_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq("id", cover.standbyShift.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("roster_shifts").insert(payload);
        if (error) throw error;
      }

      toast({
        title: "Cover assigned",
        description: `${displayName(cover.driver)} is covering ${driverDisplayName(absentShift)}`,
      });
      onCoverCreated();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to assign cover", variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  if (!absentShift) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-gray-900 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-emerald-400" />
            Find Cover
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Absent shift summary */}
          <div className="bg-amber-950/30 border border-amber-700/50 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="text-amber-200">
                  <span className="font-semibold">{driverDisplayName(absentShift)}</span> is{" "}
                  <Badge variant="outline" className="text-[10px] border-amber-500 text-amber-300 mx-0.5">
                    {absentShift.shift_type_label}
                  </Badge>{" "}
                  on {format(new Date(`${dateStr}T00:00:00`), "EEEE d MMM")}
                </p>
                <p className="text-xs text-amber-300/80 mt-0.5">
                  Pick an available driver below to create a published cover shift.
                </p>
              </div>
            </div>
          </div>

          {/* Cover shift template */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Vehicle</Label>
              <Select value={vehicleNumber || "__NONE__"} onValueChange={(v) => setVehicleNumber(v === "__NONE__" ? "" : v)}>
                <SelectTrigger className="bg-gray-800 border-gray-600 mt-1 h-9">
                  <SelectValue placeholder="Pick vehicle" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="__NONE__">No vehicle</SelectItem>
                  {availableVehicles.map((v) => (
                    <SelectItem key={v.vehicle_number} value={v.vehicle_number}>
                      {v.vehicle_number}
                      {v.vehicle_type ? ` • ${v.vehicle_type}` : ""}
                      {v.transmission_type ? ` • ${v.transmission_type}` : ""}
                      {usualVehicle?.vehicle_number === v.vehicle_number ? "  (usual)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Shift Type</Label>
              <Select value={shiftTypeCode} onValueChange={setShiftTypeCode}>
                <SelectTrigger className="bg-gray-800 border-gray-600 mt-1 h-9">
                  <SelectValue placeholder="Pick type" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {shiftTypes
                    .filter((st) => st.is_active && st.counts_as_worked_hours !== false)
                    .map((st) => (
                      <SelectItem key={st.code} value={st.code}>
                        {st.code} — {st.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">Start</Label>
              <Input
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="bg-gray-800 border-gray-600 mt-1 h-9"
              />
            </div>
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-wider">End</Label>
              <Input
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="bg-gray-800 border-gray-600 mt-1 h-9"
              />
            </div>
          </div>

          {/* Candidate list */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-gray-300 text-xs uppercase tracking-wider">
                Available Drivers ({candidateDrivers.length})
              </Label>
              {selectedVehicle?.transmission_type?.toLowerCase() === "manual" && (
                <span className="text-[10px] text-amber-400">Manual vehicle — manual-qualified only</span>
              )}
            </div>

            {candidateDrivers.length === 0 ? (
              <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-6 text-center text-sm text-gray-400">
                No free or standby drivers available for this day
                {selectedVehicle?.transmission_type?.toLowerCase() === "manual"
                  ? " who can drive manual"
                  : ""}
                .
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
                {candidateDrivers.map((c) => (
                  <div
                    key={c.driver.driver_name}
                    className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 ${
                      c.status === "free"
                        ? "bg-emerald-950/20 border-emerald-700/40"
                        : "bg-blue-950/20 border-blue-700/40"
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {selectedVehicleIconIsTruck ? (
                        <Truck className="h-4 w-4 text-gray-400 shrink-0" />
                      ) : (
                        <Car className="h-4 w-4 text-gray-400 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">
                          {displayName(c.driver)}
                        </p>
                        <p className="text-[11px] text-gray-400 truncate flex items-center gap-1.5">
                          {c.status === "free" ? (
                            <span className="text-emerald-400">No shift that day</span>
                          ) : (
                            <>
                              <Clock className="h-3 w-3 text-blue-300" />
                              <span className="text-blue-300">
                                On STANDBY {c.standbyShift?.start_hhmm}–{c.standbyShift?.end_hhmm}
                              </span>
                            </>
                          )}
                          {c.driver.can_drive_manual && (
                            <span className="text-[9px] px-1 rounded bg-gray-700 text-gray-300">MANUAL</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 h-8 shrink-0"
                      onClick={() => handleAssign(c)}
                      disabled={saving === c.driver.driver_name}
                    >
                      {saving === c.driver.driver_name ? (
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      ) : c.status === "standby" ? (
                        "Convert Standby"
                      ) : (
                        "Assign Cover"
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-2">
              Cover shift will be created at <span className="text-gray-300">{formatTime12(start)} – {formatTime12(end)}</span>
              {vehicleNumber ? <> with vehicle <span className="text-gray-300">{vehicleNumber}</span></> : <> with <span className="text-gray-300">no vehicle</span></>}
              , status <span className="text-emerald-400">Published</span>.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FindCoverDialog;
